// Pure handler factories for ReportActions — separated from the JSX so the
// component body stays under ~50 lines. Each factory takes the row context
// and returns a click handler that performs an API call + toast.

import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadPdf = (assessmentId) =>
  apiClient.get(`/admin/reports/${assessmentId}/pdf`, { responseType: 'blob' })
    .then(r => {
      // Prefer the server-suggested filename so it matches AWSAI-YYYY-NNNNNN-OrgName.pdf
      const disp = r.headers?.['content-disposition'] || '';
      const match = /filename\s*=\s*"?([^";]+)"?/i.exec(disp);
      const filename = match?.[1] || `AWSAI_Professional_Report_${assessmentId}.pdf`;
      triggerBlobDownload(r.data, filename);
      toast.success('Professional Report downloaded');
    })
    .catch(err => {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Admin authentication required.');
      } else {
        toast.error('Failed to generate the Professional Report. Please try again.');
      }
    });

export const downloadDocx = (assessmentId) =>
  apiClient.get(`/admin/reports/${assessmentId}/docx`, { responseType: 'blob' })
    .then(r => {
      const disp = r.headers?.['content-disposition'] || '';
      const match = /filename\s*=\s*"?([^";]+)"?/i.exec(disp);
      const filename = match?.[1] || `AWSAI_Professional_Report_${assessmentId}.docx`;
      triggerBlobDownload(r.data, filename);
      toast.success('Editable DOCX downloaded');
    })
    .catch(err => {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Admin authentication required.');
      } else {
        toast.error('Failed to generate the DOCX. Please try again.');
      }
    });

export const togglePreview = (assessmentId, mode, onChanged, setBusy) => {
  if (mode === 'real_paid') return Promise.resolve();
  const path = mode === 'unlocked' ? 'preview-lock' : 'preview-unlock';
  setBusy(true);
  return apiClient.post(`/admin/assessments/${assessmentId}/${path}`)
    .then(() => {
      toast.success(mode === 'unlocked' ? 'Preview re-locked' : 'Preview unlocked — open the report to QA');
      onChanged?.();
    })
    .catch(err => toast.error(err.response?.data?.detail || 'Failed to toggle preview'))
    .finally(() => setBusy(false));
};

export const resendReportEmail = (assessmentId, onChanged, setBusy) => {
  setBusy(true);
  return apiClient.post(`/admin/assessments/${assessmentId}/resend-report-email`)
    .then(r => {
      if (r.data?.sendgrid_configured === false) {
        toast.warning('Queued, but SendGrid API key is not configured — email will not actually send.');
      } else {
        toast.success('Report email queued for delivery.');
      }
      onChanged?.();
    })
    .catch(err => toast.error(err.response?.data?.detail || 'Failed to queue email'))
    .finally(() => setBusy(false));
};

// Decide what the preview toggle should do for a given row.
// - Real paid customer (paid + no preview flag) → hide toggle (safety).
// - preview_unlock=true → show "Lock Preview" (re-lock).
// - else → show "Preview as Paid" (unlock for QA).
export const getPreviewMode = ({ paymentStatus, previewUnlock }) => {
  if (paymentStatus === 'paid' && !previewUnlock) return 'real_paid';
  if (previewUnlock) return 'unlocked';
  return 'locked';
};
